import React from 'react';
import {
    Box,
    Button,
    ButtonProps,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    LinearProgress,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    Switch,
    TextField,
    TextFieldProps,
    Typography,
    useTheme
} from '@mui/material';
import { useSettingsContext } from 'contexts/settingsContext';
import * as tenantService from 'services/tenant-service';
import * as settingService from 'services/settings-service';
import { setting } from 'services/settings-service';
import { useAuthContext } from 'contexts/authContext';
import { useTranslation } from 'react-i18next';
import * as Styles from 'pages/development/settings/Settings.styles';
import { IPropsSettings } from '../Settings';
import { VisibilityOff, Visibility } from '@mui/icons-material';

const parseSigningMethods = (value: string): string[] => value.split(',').filter(Boolean);


const SigningTab = (props: IPropsSettings) => {
    const { tenantId } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const { getSetting, updateSetting } = useSettingsContext();
    const { checkAuth } = useAuthContext();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [initialUseConnective, setInitialUseConnective] = React.useState<boolean>(false);
    const [useConnective, setUseConnective] = React.useState<boolean>(false);

    const signingMethods = process.env.REACT_APP_ENV !== 'production' ?
        ["beid", "RemoteHashSigning:itsmeE2E", "manual", "RemoteHashSigning:itsme_DigitaalVlaanderen"] :
        ["beid", "RemoteHashSigning:itsme", "RemoteHashSigning:itsme_DigitaalVlaanderen"];

    const [signingMethodsExternal, setSigningMethodsExternal] = React.useState<string[]>([]);
    const [signingMethodsInternal, setSigningMethodsInternal] = React.useState<string[]>([]);
    const [urlConnective, setUrlConnective] = React.useState<string>('');
    const [urlTokenConnective, setUrlTokenConnective] = React.useState<string>('');
    const [clientIdConnective, setClientIdConnective] = React.useState<string>('');
    const [clientSecretConnective, setClientSecretConnective] = React.useState<string>('');
    const [contactGroupConnective, setContactGroupConnective] = React.useState<string>('');
    const [defaultUserConnective, setDefaultUserConnective] = React.useState<string>('');
    const [themeCode, setThemeCode] = React.useState<string>('');
    const [scope, setScope] = React.useState<string>('');
    const [grantType, setGrantType] = React.useState<string>('');

    const [initialSigningMethodsExternal, setInitialSigningMethodsExternal] = React.useState<string[]>([]);
    const [initialSigningMethodsInternal, setInitialSigningMethodsInternal] = React.useState<string[]>([]);
    const [initialUrlConnective, setInitialUrlConnective] = React.useState<string>('');
    const [initialUrlTokenConnective, setInitialUrlTokenConnective] = React.useState<string>('');
    const [initialClientIdConnective, setInitialClientIdConnective] = React.useState<string>('');
    const [initialClientSecretConnective, setInitialClientSecretConnective] = React.useState<string>('');
    const [initialContactGroupConnective, setInitialContactGroupConnective] = React.useState<string>('');
    const [initialDefaultUserConnective, setInitialDefaultUserConnective] = React.useState<string>('');
    const [initialThemeCode, setInitialThemeCode] = React.useState<string>('');
    const [initialScope, setInitialScope] = React.useState<string>('');
    const [initialGrantType, setInitialGrantType] = React.useState<string>('');

    const [showPassword, setShowPassword] = React.useState(false);

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const arraysEqual = (a: string[], b: string[]): boolean => {
        if (a.length !== b.length) return false;
        const sortedA = [...a].sort();
        const sortedB = [...b].sort();
        return sortedA.every((value, index) => value === sortedB[index]);
    };

    React.useEffect(() => {
        setLoading(true);
        settingService.getSettings(tenantId).then((res) => {
            setUseConnective(res.useConnective);
            setInitialUseConnective(res.useConnective);
        });
        tenantService.getConnectiveInfo(tenantId).then((res) => {
            setUpdatedData(res);
            setInitialData(res);
            setLoading(false);
        });
    }, [tenantId]);

    const setInitialData = (dto: tenantService.tenantConnectiveDto) => {
        setInitialSigningMethodsExternal(parseSigningMethods(dto.signingMethodsExternal));
        setInitialSigningMethodsInternal(parseSigningMethods(dto.signingMethodsInternal));
        setInitialUrlConnective(dto.urlApi);
        setInitialUrlTokenConnective(dto.urlToken);
        setInitialClientIdConnective(dto.clientId);
        setInitialClientSecretConnective(dto.clientSecret);
        setInitialContactGroupConnective(dto.contactGroup);
        setInitialDefaultUserConnective(dto.defaultUser);
        setInitialThemeCode(dto.themeCode);
        setInitialScope(dto.scope);
        setInitialGrantType(dto.grantType);
    }

    const setUpdatedData = (dto: tenantService.tenantConnectiveDto) => {
        setSigningMethodsExternal(parseSigningMethods(dto.signingMethodsExternal));
        setSigningMethodsInternal(parseSigningMethods(dto.signingMethodsInternal));
        setUrlConnective(dto.urlApi);
        setUrlTokenConnective(dto.urlToken);
        setClientIdConnective(dto.clientId);
        setClientSecretConnective(dto.clientSecret);
        setContactGroupConnective(dto.contactGroup);
        setDefaultUserConnective(dto.defaultUser);
        setThemeCode(dto.themeCode);
        setScope(dto.scope);
        setGrantType(dto.grantType);
    }

    const checkDataChanged = () => {
        var changed = false;
        if (initialUrlConnective !== urlConnective ||
            initialUrlTokenConnective !== urlTokenConnective ||
            initialClientIdConnective !== clientIdConnective ||
            initialClientSecretConnective !== clientSecretConnective ||
            initialContactGroupConnective !== contactGroupConnective ||
            initialDefaultUserConnective !== defaultUserConnective ||
            !arraysEqual(signingMethodsExternal, initialSigningMethodsExternal) ||
            !arraysEqual(signingMethodsInternal, initialSigningMethodsInternal) ||
            initialThemeCode !== themeCode ||
            initialScope !== scope ||
            initialGrantType !== grantType) {
            changed = true;
        }
        return changed;
    }

    const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>, enabled: boolean) => {
        setUseConnective(enabled);
    }

    const handleUpdate = () => {
        if (initialUseConnective !== useConnective) {
            settingService.updateConnective(useConnective, tenantId).then((res) => {
                setInitialUseConnective(res.useConnective);
                setUseConnective(res.useConnective);
                // Only update settings if same tenantId in getSetting
                if (getSetting<string>('tenantId') === tenantId)  {
                    updateSetting<boolean>('useConnective', res.useConnective);
                }
            });
        }

        if (checkDataChanged()) {
            tenantService.updateConnectiveInfo({
                id: tenantId,
                signingMethodsExternal: signingMethodsExternal.join(','),
                signingMethodsInternal: signingMethodsInternal.join(','),
                urlApi: urlConnective,
                urlToken: urlTokenConnective,
                clientId: clientIdConnective,
                clientSecret: clientSecretConnective,
                contactGroup: contactGroupConnective,
                defaultUser: defaultUserConnective,
                themeCode: themeCode,
                scope: scope,
                grantType: grantType
            }).then((res) => {
                setInitialData(res);
            });
        }
    }

    const handleCancel = () => {
        setUseConnective(initialUseConnective);
        setUpdatedData({
            id: tenantId,
            signingMethodsExternal: initialSigningMethodsExternal.join(','),
            signingMethodsInternal: initialSigningMethodsInternal.join(','),
            urlApi: initialUrlConnective,
            urlToken: initialUrlTokenConnective,
            clientId: initialClientIdConnective,
            clientSecret: initialClientSecretConnective,
            contactGroup: initialContactGroupConnective,
            defaultUser: initialDefaultUserConnective,
            themeCode: initialThemeCode,
            scope: initialScope,
            grantType: initialGrantType
        });
    }

    const handleSigningMethodsExternalChange = (event: SelectChangeEvent<string[]>) => {
        const { target: { value }, } = event;
        const methods = typeof value === 'string' ? value.split(',') : value;
        if (value.length === 0) {
            return;
        }
        setSigningMethodsExternal(methods);
    }

    const handleSigningMethodsInternalChange = (event: SelectChangeEvent<string[]>) => {
        const { target: { value }, } = event;
        const methods = typeof value === 'string' ? value.split(',') : value;
        if (value.length === 0) {
            return;
        }
        setSigningMethodsInternal(methods);
    }

    const updateProps: ButtonProps = {
        onClick: handleUpdate,
        variant: 'contained',
        color: 'success',
        sx: {
            marginLeft: '1rem',
        }
    }

    const textFieldProps = (disabled: boolean = true): TextFieldProps => {
        return {
            disabled,
            size: 'small',
            sx: {
                width: '50%',
                backgroundColor: 'white'
            }
        }
    }

    return (
        <Box>
            <Typography variant='h4'>
                {t('settings.sign.title')}
            </Typography>
            <Box>
                {t('settings.sign.description')}
            </Box>
            {loading && (
                <Box sx={{ marginTop: '1rem' }}>
                    <LinearProgress/>
                </Box>
            )}
            {!loading && (
                <Box sx={{ marginTop: '1rem' }}>
                    <Grid container spacing={2} sx={ Styles.infoWrapper(theme) }>
                        <Grid item xs={12} md={3}>
                            <Box>
                                {t('settings.sign.useConnective')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <FormControl>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            disabled={!checkAuth(['Administrator'])}
                                            onChange={handleSwitchChange}
                                            checked={useConnective}
                                        />
                                    }
                                    label=""
                                />
                                <FormHelperText sx={{ whiteSpace: 'pre-line' }}>
                                    {t('settings.sign.useConnectiveHelper')}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                    </Grid>
                    {(useConnective && (
                        <React.Fragment>
                            <Grid container spacing={2} sx={ Styles.infoWrapper(theme) }>
                                <Grid item xs={12} md={3}>
                                    <Box>
                                        {t('settings.sign.signMethodsExternal')}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={9}>
                                    <Select
                                        labelId="signing-methods-external"
                                        id="signing-methods-external"
                                        multiple
                                        value={signingMethodsExternal}
                                        size='small'
                                        sx={{ width: '50%', backgroundColor: 'white' }}
                                        onChange={handleSigningMethodsExternalChange}
                                        input={<OutlinedInput />}
                                        renderValue={(selected) => selected.map((value) => t('settings.sign.' + value.replace(":", ""))).join(', ')}
                                        onBlur={(e) => e.stopPropagation()}
                                    >
                                        {signingMethods.map((item) => (
                                            <MenuItem key={item} value={item} sx={{ paddingRight: "15px" }}>
                                                <Checkbox checked={signingMethodsExternal.indexOf(item) > -1} />
                                                <ListItemText primary={t('settings.sign.' + item.replace(":", ""))} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormControl>
                                        <FormHelperText>
                                            {t('settings.sign.signMethodsExternalHelper')}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Box>
                                        {t('settings.sign.signMethodesInternal')}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={9}>
                                    <Select
                                        labelId="signing-methods-internal"
                                        id="signing-methods-internal"
                                        multiple
                                        value={signingMethodsInternal}
                                        size='small'
                                        sx={{ width: '50%', backgroundColor: 'white' }}
                                        onChange={handleSigningMethodsInternalChange}
                                        input={<OutlinedInput />}
                                        renderValue={(selected) => selected.map((value) => t('settings.sign.' + value.replace(":", ""))).join(', ')}
                                        onBlur={(e) => e.stopPropagation()}
                                    >
                                        {signingMethods.map((item) => (
                                            <MenuItem key={item} value={item} sx={{ paddingRight: "15px" }}>
                                                <Checkbox checked={signingMethodsInternal.indexOf(item) > -1} />
                                                <ListItemText primary={t('settings.sign.' + item.replace(":", ""))} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormControl>
                                        <FormHelperText>
                                            {t('settings.sign.signMethodesInternalHelper')}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Grid container spacing={2} sx={ Styles.infoWrapper(theme) }>
                                <Grid item xs={12} md={3}>
                                    <Box>
                                        {t('settings.sign.urlApi')}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={9}>
                                    <Grid item>
                                        <TextField value={urlConnective} onChange={(e) => setUrlConnective(e.target.value)} {...textFieldProps(false)}/>
                                    </Grid>
                                    <FormControl>
                                        <FormHelperText>
                                            {t('settings.sign.urlApiHelper')}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Box>
                                        {t('settings.sign.urlToken')}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={9}>
                                    <Grid item>
                                        <TextField value={urlTokenConnective} onChange={(e) => setUrlTokenConnective(e.target.value)} {...textFieldProps(false)}/>
                                    </Grid>
                                    <FormControl>
                                        <FormHelperText>
                                            {t('settings.sign.urlTokenHelper')}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Box>
                                        {t('settings.sign.clientId')}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={9}>
                                    <Grid item>
                                        <TextField value={clientIdConnective} onChange={(e) => setClientIdConnective(e.target.value)} {...textFieldProps(false)}/>
                                    </Grid>
                                    <FormControl>
                                        <FormHelperText>
                                            {t('settings.sign.clientIdHelper')}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Box>
                                        {t('settings.sign.clientSecret')}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={9}>
                                    <Grid item>
                                        <OutlinedInput
                                            id="secret"
                                            type={showPassword ? 'text' : 'password'}
                                            value={clientSecretConnective} 
                                            onChange={(e) => setClientSecretConnective(e.target.value)} 
                                            size='small'
                                            sx={{
                                                width: '50%',
                                                backgroundColor: 'white'
                                            }}
                                            endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                aria-label={
                                                    showPassword ? 'hide the password' : 'display the password'
                                                }
                                                onClick={handleClickShowPassword}
                                                onMouseDown={handleMouseDownPassword}
                                                onMouseUp={handleMouseUpPassword}
                                                edge="end"
                                                >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                            }
                                        />
                                    </Grid>
                                    <FormControl>
                                        <FormHelperText>
                                            {t('settings.sign.clientSecretHelper')}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Box>
                                        {t('settings.sign.scope')}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={9}>
                                    <Grid item>
                                        <TextField value={scope} onChange={(e) => setScope(e.target.value)} {...textFieldProps(false)}/>
                                    </Grid>
                                    <FormControl>
                                        <FormHelperText>
                                            {t('settings.sign.scopeHelper')}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Box>
                                        {t('settings.sign.grantType')}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={9}>
                                    <Grid item>
                                        <TextField value={grantType} onChange={(e) => setGrantType(e.target.value)} {...textFieldProps(false)}/>
                                    </Grid>
                                    <FormControl>
                                        <FormHelperText>
                                            {t('settings.sign.grantTypeHelper')}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Grid container spacing={2} sx={ Styles.infoWrapper(theme) }>
                                <Grid item xs={12} md={3}>
                                    <Box>
                                        {t('settings.sign.contactgroup')}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={9}>
                                    <Grid item>
                                        <TextField value={contactGroupConnective} onChange={(e) => setContactGroupConnective(e.target.value)} {...textFieldProps(false)}/>
                                    </Grid>
                                    <FormControl>
                                        <FormHelperText>
                                            {t('settings.sign.contactgroupHelper')}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Box>
                                        {t('settings.sign.themeCode')}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={9}>
                                    <Grid item>
                                        <TextField value={themeCode} onChange={(e) => setThemeCode(e.target.value)} {...textFieldProps(false)}/>
                                    </Grid>
                                    <FormControl>
                                        <FormHelperText>
                                            {t('settings.sign.themeCodeHelper')}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Box>
                                        {t('settings.sign.defaultUser')}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={9}>
                                    <Grid item>
                                        <TextField value={defaultUserConnective} onChange={(e) => setDefaultUserConnective(e.target.value)} {...textFieldProps(false)}/>
                                    </Grid>
                                    <FormControl>
                                        <FormHelperText>
                                            {t('settings.sign.defaultUserHelper')}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </React.Fragment>
                    ))}
                    {(useConnective !== initialUseConnective || checkDataChanged()
                    ) && (
                        <Grid item xs={12} md={12} sx={{
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <Button onClick={handleCancel} variant='contained' color='cancel' sx={{marginRight: '1rem'}}>
                                {t('buttons.cancel')}
                            </Button>
                            <Button {...updateProps}>
                                {t('buttons.save')}
                            </Button>
                        </Grid>
                    )}
                </Box>
            )}
        </Box>
    )
}

export default SigningTab;